import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "snip_cart_v2";

const lineKey = (i) => `${i.product_id}::${i.variant_size || ""}::${i.color || ""}`;

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const lastSyncedUserId = useRef(null);
  const syncDebounce = useRef(null);

  // Hydrate from localStorage once on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch { /* ignore */ }
    setHydrated(true);
  }, []);

  // Persist to localStorage on every change
  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items, hydrated]);

  // On login: merge server cart with local cart (server is authoritative for prior state).
  // On logout: just drop the in-memory user_id tracker.
  useEffect(() => {
    if (!hydrated) return;
    const uid = user?.id || null;
    if (uid && lastSyncedUserId.current !== uid) {
      // Newly authenticated — fetch server cart and merge by lineKey, summing quantities
      api.get("/cart").then(({ data: serverItems }) => {
        if (!Array.isArray(serverItems) || serverItems.length === 0) {
          // No server cart — push local (if any) to server
          lastSyncedUserId.current = uid;
          if (items.length > 0) pushToServer(items);
          return;
        }
        setItems((local) => {
          // Build map from local for quick lookup
          const map = new Map(local.map((i) => [lineKey(i), { ...i }]));
          for (const s of serverItems) {
            // Server items only have minimal fields — we need product info from local OR fetch
            const key = `${s.product_id}::${s.variant_size || ""}::${s.color || ""}`;
            if (map.has(key)) {
              const cur = map.get(key);
              map.set(key, { ...cur, quantity: cur.quantity + (s.quantity || 0) });
            } else {
              // Server has an item we don't know about locally — fetch its product
              // We'll fire-and-forget rehydrate via product lookup
              map.set(key, {
                product_id: s.product_id,
                variant_size: s.variant_size || null,
                color: s.color || null,
                quantity: s.quantity || 0,
                title: "Loading…",
                price: 0,
                image_url: "",
                is_digital: false,
                slug: "",
              });
            }
          }
          const merged = Array.from(map.values());
          // Rehydrate any "Loading…" items by fetching product info
          merged.forEach((item, idx) => {
            if (item.title === "Loading…" && item.product_id) {
              api.get(`/products/${item.product_id}`).then(({ data: p }) => {
                setItems((prev) => prev.map((x, i) => i === idx ? {
                  ...x,
                  title: p.title,
                  price: p.price,
                  image_url: p.image_url,
                  is_digital: p.is_digital,
                  slug: p.slug,
                } : x));
              }).catch(() => {});
            }
          });
          return merged;
        });
        lastSyncedUserId.current = uid;
      }).catch(() => {
        lastSyncedUserId.current = uid;
      });
    } else if (!uid) {
      lastSyncedUserId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, hydrated]);

  // Push to server (debounced) whenever items change AND user is logged in
  const pushToServer = (current) => {
    if (!user?.id) return;
    const payload = {
      items: current.map((i) => ({
        product_id: i.product_id,
        quantity: i.quantity,
        variant_size: i.variant_size || null,
        color: i.color || null,
      })),
    };
    api.put("/cart", payload).catch(() => { /* silent — local is source of truth on failure */ });
  };

  useEffect(() => {
    if (!hydrated || !user?.id) return;
    if (lastSyncedUserId.current !== user.id) return;  // wait for initial merge
    if (syncDebounce.current) clearTimeout(syncDebounce.current);
    syncDebounce.current = setTimeout(() => pushToServer(items), 600);
    return () => { if (syncDebounce.current) clearTimeout(syncDebounce.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, user?.id, hydrated]);

  const add = (product, quantity = 1, variant_size = null, opts = {}) => {
    setItems((prev) => {
      const incoming = {
        product_id: product.id,
        slug: product.slug,
        title: product.title,
        price: product.price,
        image_url: opts.image_url || product.image_url,
        is_digital: product.is_digital,
        variant_size: variant_size || null,
        color: opts.color || null,
        quantity,
      };
      const idx = prev.findIndex((i) => lineKey(i) === lineKey(incoming));
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, incoming];
    });
    if (opts.silent !== true) setOpen(true);
  };

  const update = (key, quantity) => {
    setItems((prev) => prev.map((i) => (lineKey(i) === key ? { ...i, quantity } : i)).filter((i) => i.quantity > 0));
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => lineKey(i) !== key));
  const clear = () => {
    setItems([]);
    if (user?.id) api.delete("/cart").catch(() => {});
  };

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const hasPhysical = useMemo(() => items.some((i) => !i.is_digital), [items]);
  const shipping = useMemo(() => {
    if (!hasPhysical) return 0;
    if (subtotal >= 50) return 0;
    return 5.99;
  }, [hasPhysical, subtotal]);

  return (
    <CartContext.Provider value={{ items, add, update, remove, clear, subtotal, shipping, hasPhysical, count, open, setOpen, lineKey }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
